<template>
  <UModal v-model:open="open" :ui="{ content: 'sm:max-w-4xl' }">
    <template #header>
      <h4 class="font-semibold">Advanced User Settings</h4>
    </template>

    <template #body>
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 max-h-[70vh] overflow-y-auto text-sm"
      >
        <!-- Left column -->
        <div class="flex flex-col gap-4">
          <!-- Stick Settings -->
          <UiBox title="Stick Settings">
            <SettingRow label="Units" help="Display actual units on stick display">
              <USwitch v-model="local.stickUnits" size="sm" />
            </SettingRow>
            <SettingRow label="Stick Trails" help="Show stick trails">
              <USwitch v-model="local.stickTrails" size="sm" />
            </SettingRow>
            <SettingRow label="Invert Yaw" help="Invert yaw in stick display">
              <USwitch v-model="local.stickInvertYaw" size="sm" />
            </SettingRow>
            <SettingRow label="Mode">
              <template #trailing>
                <img
                  :src="`./images/stick_modes/Mode_${local.stickMode}.png`"
                  class="w-48 h-40 object-contain"
                  alt="Stick mode preview"
                />
              </template>
              <USelect
                v-model="local.stickMode"
                :items="stickModeOptions"
                :ui="{ content: 'z-[300]' }"
                size="sm"
                class="min-w-24"
              />
            </SettingRow>
          </UiBox>

          <!-- Display Settings -->
          <UiBox title="Display">
            <SettingRow label="Legend units" help="Display actual units on legend">
              <USwitch v-model="local.legendUnits" size="sm" />
            </SettingRow>
            <SettingRow label="Gradient" help="Show the gradient background">
              <USwitch v-model="local.drawGradient" size="sm" />
            </SettingRow>
            <SettingRow label="TimeBar" help="Show the vertical timebar">
              <USwitch v-model="local.drawVerticalBar" size="sm" />
            </SettingRow>
          </UiBox>

          <!-- Preferences -->
          <UiBox title="Preferences">
            <SettingRow label="Speed units">
              <USelect
                v-model="local.speedUnits"
                :items="speedOptions"
                :ui="{ content: 'z-[300]' }"
                size="sm"
                class="min-w-24"
              />
            </SettingRow>
            <SettingRow label="Altitude units">
              <USelect
                v-model="local.altitudeUnits"
                :items="altitudeOptions"
                :ui="{ content: 'z-[300]' }"
                size="sm"
                class="min-w-24"
              />
            </SettingRow>
            <SettingRow label="Dark mode">
              <USelect
                v-model="local.darkMode"
                :items="darkModeOptions"
                :ui="{ content: 'z-[300]' }"
                size="sm"
                class="min-w-40"
              />
            </SettingRow>
          </UiBox>

           <!-- Video Overlay -->
          <UiBox title="Video Overlay">
            <SettingRow label="Watermark" help="Show the watermark">
              <USwitch v-model="local.drawWatermark" size="sm" />
            </SettingRow>
            <div v-if="local.drawWatermark" class="flex flex-col gap-2 mt-2 ml-6">
              <div class="flex items-center gap-3">
                <label class="text-sm">Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  class="text-sm"
                  @change="onLogoChange"
                />
              </div>
              <img
                v-if="local.watermark.logo"
                :src="local.watermark.logo"
                class="w-24 h-16 object-contain border rounded border-default"
                alt="Watermark logo"
              />
              <PercentInput
                v-model="local.watermark.transparency"
                label="Transparency"
              />
            </div>
          </UiBox>

          <!-- Craft Settings -->
          <UiBox title="Craft Settings">
            <SettingRow label="Initial Attitude" help="Direction the 3D craft model faces at zero yaw">
              <USelect
                v-model="local.craft.facing"
                :items="craftFacingOptions"
                :ui="{ content: 'z-[300]' }"
                size="sm"
                class="min-w-36"
              />
            </SettingRow>
          </UiBox>

             <!-- Overlay Positions (collapsible) -->
          <UiBox title="Overlay Positions">
            <details>
              <summary class="text-xs cursor-pointer text-dimmed hover:text-default select-none">
                Adjust position and size of overlays
              </summary>
              <div class="grid grid-cols-[4.5rem_repeat(3,auto)] gap-x-1 gap-y-1.5 items-center text-xs mt-3">
                <template v-for="row in positionRows" :key="row.label">
                  <span class="text-dimmed truncate">{{ row.label }}</span>
                  <div class="flex items-center gap-0.5">
                    <span class="text-dimmed w-6">Top</span>
                    <UInputNumber :model-value="pv(row.obj[row.topKey])" :min="0" :max="100" :step="1" :format-options="{ useGrouping: false }" size="xs" orientation="vertical" :ui="{ root: 'w-12' }" @update:model-value="row.obj[row.topKey] = `${$event}%`" />
                    <span class="text-dimmed">%</span>
                  </div>
                  <div class="flex items-center gap-0.5">
                    <span class="text-dimmed w-6">Left</span>
                    <UInputNumber :model-value="pv(row.obj[row.leftKey])" :min="0" :max="100" :step="1" :format-options="{ useGrouping: false }" size="xs" orientation="vertical" :ui="{ root: 'w-12' }" @update:model-value="row.obj[row.leftKey] = `${$event}%`" />
                    <span class="text-dimmed">%</span>
                  </div>
                  <div v-if="row.sizeKey" class="flex items-center gap-0.5">
                    <span class="text-dimmed w-6">{{ row.sizeLabel || 'Size' }}</span>
                    <UInputNumber :model-value="pv(row.obj[row.sizeKey])" :min="0" :max="100" :step="1" :format-options="{ useGrouping: false }" size="xs" orientation="vertical" :ui="{ root: 'w-12' }" @update:model-value="row.obj[row.sizeKey] = `${$event}%`" />
                    <span class="text-dimmed">%</span>
                  </div>
                  <div v-else />
                </template>
              </div>
            </details>
          </UiBox>

        </div>

        <!-- Right column -->
        <div class="flex flex-col gap-4">
          <!-- Analyser Settings -->
          <UiBox title="Analyser Settings">
            <SettingRow label="Hanning" help="Use Hanning window for analyser">
              <USwitch v-model="local.analyserHanning" size="sm" />
            </SettingRow>
          </UiBox>

          <!-- Trim Settings -->
          <UiBox title="Trim Settings">
            <SettingRow label="Auto Trim">
              <USwitch v-model="local.autoTrim" size="sm" />
            </SettingRow>
            <p class="text-xs text-dimmed mt-1">
              When a log is opened, automatically set the In/Out points (same as the I and O
              keys) using the events below. Governor State is only logged when a governor is
              active - use Arm/Disarm if you're not running one.
            </p>
            <div v-if="local.autoTrim" class="flex flex-col gap-2 mt-2">
              <SettingRow label="Start Event">
                <USelect
                  v-model="local.autoTrimStartEvent"
                  :items="autoTrimEventOptions"
                  :ui="{ content: 'z-[300]' }"
                  size="sm"
                  class="min-w-44"
                />
              </SettingRow>
              <SettingRow label="Stop Event">
                <USelect
                  v-model="local.autoTrimStopEvent"
                  :items="autoTrimEventOptions"
                  :ui="{ content: 'z-[300]' }"
                  size="sm"
                  class="min-w-44"
                />
              </SettingRow>
              <SettingRow label="Offset">
                <USelect
                  v-model="local.autoTrimOffset"
                  :items="autoTrimOffsetOptions"
                  :ui="{ content: 'z-[300]' }"
                  size="sm"
                  class="min-w-32"
                />
              </SettingRow>
              <p class="text-xs text-dimmed">
                Trim starts this many seconds after the start event, and ends this many seconds
                before the stop event.
              </p>
            </div>
          </UiBox>

          <!-- AI Analysis Settings -->
          <UiBox title="AI Analysis Settings">
            <div class="flex flex-col gap-1">
              <label class="text-sm">Anthropic API Key</label>
              <UInput
                v-model="local.aiApiKey"
                type="password"
                autocomplete="off"
                placeholder="sk-ant-..."
                size="sm"
                class="w-full"
              />
              <p class="text-xs text-dimmed">
                Used by the Tuning Log's "AI Analysis" feature. Stored locally on this computer
                only. Don't have a key, or need to buy credits? Get one at
                <a
                  href="https://platform.claude.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary underline"
                  >platform.claude.com</a
                >.
              </p>
            </div>
            <SettingRow label="Model">
              <USelect
                v-model="local.aiModel"
                :items="aiModelOptions"
                :ui="{ content: 'z-[300]' }"
                size="sm"
                class="min-w-56"
              />
            </SettingRow>
            <div class="flex flex-col gap-1">
              <SettingRow label="Effort">
                <USelect
                  v-model="local.aiEffort"
                  :items="aiEffortOptions"
                  :ui="{ content: 'z-[300]' }"
                  size="sm"
                  class="min-w-44"
                />
              </SettingRow>
              <p class="text-xs text-dimmed">
                How much the model reasons before answering. Higher effort can give more thorough
                tuning advice, at the cost of a slower, more expensive response. Ignored on models
                that don't support it.
              </p>
            </div>
            <div class="flex flex-col gap-1">
              <SettingRow label="History graphs">
                <USelect
                  v-model="local.aiHistoryImageLimit"
                  :items="aiHistoryImageLimitOptions"
                  :ui="{ content: 'z-[300]' }"
                  size="sm"
                  class="min-w-44"
                />
              </SettingRow>
              <p class="text-xs text-dimmed">
                How many earlier Tuning Log entries send their step response graph to the AI. Older
                entries still send their configuration, notes and earlier analysis as text, so the
                history isn't lost - fewer graphs means fewer tokens and a cheaper request.
              </p>
            </div>
            <div class="flex flex-col gap-1">
              <SettingRow label="Isolated flight rate">
                <USelect
                  v-model="local.aiSliceSampleRate"
                  :items="aiSliceSampleRateOptions"
                  :ui="{ content: 'z-[300]' }"
                  size="sm"
                  class="min-w-44"
                />
              </SettingRow>
              <p class="text-xs text-dimmed">
                When you attach the isolated block (Ctrl+I / Ctrl+O) to an AI request, the fields
                shown in the current workspace are averaged down to this rate. Higher rates keep
                more detail but cost more tokens - 100 Hz is plenty for setpoint tracking.
              </p>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm">Custom Skill IDs (optional)</label>
              <div v-for="(skillId, idx) in local.aiSkillIds" :key="idx" class="flex items-center gap-2">
                <UInput
                  v-model="local.aiSkillIds[idx]"
                  autocomplete="off"
                  placeholder="skill_..."
                  size="sm"
                  class="w-full"
                />
                <UButton
                  variant="ghost"
                  color="error"
                  icon="i-lucide-trash-2"
                  size="xs"
                  @click="local.aiSkillIds.splice(idx, 1)"
                />
              </div>
              <UButton
                variant="link"
                color="neutral"
                icon="i-lucide-plus"
                label="Add skill"
                size="xs"
                class="self-start -ml-2"
                @click="local.aiSkillIds.push('')"
              />
              <p class="text-xs text-dimmed">
                IDs of custom Agent Skills already uploaded to your Anthropic account (Console
                &rarr; Skills, or the Skills API) - not Claude Code skills. When set, they're all
                loaded into every AI Analysis request via a code-execution container, which adds a
                small amount of extra cost/latency per skill. Leave empty to disable.
              </p>
            </div>
          </UiBox>

          <!-- Cloud Storage Settings -->
          <UiBox title="Cloud Storage (GitHub)">
            <p class="text-xs text-dimmed">
              Keep your Tuning Logs in a GitHub repository, so they're backed up and shared between
              computers. Logs are grouped by craft name, and only the logs for the heli you're
              working on are downloaded. Changes made offline are saved locally and uploaded next
              time you're online. Leave blank to keep logs on this computer only.
            </p>
            <div class="flex flex-col gap-1">
              <label class="text-sm">Repository</label>
              <UInput
                v-model="local.githubRepo"
                autocomplete="off"
                placeholder="owner/repo"
                size="sm"
                class="w-full"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm">Branch (optional)</label>
              <UInput
                v-model="local.githubBranch"
                autocomplete="off"
                placeholder="Repository's default branch"
                size="sm"
                class="w-full"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm">Access Token</label>
              <UInput
                v-model="local.githubToken"
                type="password"
                autocomplete="off"
                placeholder="github_pat_..."
                size="sm"
                class="w-full"
              />
              <p class="text-xs text-dimmed">
                A
                <a
                  href="https://github.com/settings/personal-access-tokens/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary underline"
                  >fine-grained personal access token</a
                >
                with access to only this repository and "Contents: Read and write" permission. A
                private repository is recommended. Stored locally on this computer only, and only
                ever sent to GitHub.
              </p>
            </div>
            <div class="flex items-center gap-2">
              <UButton
                variant="outline"
                color="neutral"
                size="xs"
                icon="i-lucide-plug"
                label="Test connection"
                :loading="githubTesting"
                :disabled="!local.githubRepo || !local.githubToken"
                @click="onTestGithub"
              />
              <span
                v-if="githubTestResult"
                class="text-xs"
                :class="githubTestResult.ok ? 'text-success' : 'text-error'"
                >{{ githubTestResult.message }}</span
              >
            </div>
            <div class="flex items-center gap-2">
              <UButton
                variant="outline"
                color="neutral"
                size="xs"
                icon="i-lucide-files"
                label="Convert logs to one file per entry"
                :loading="githubMigrating"
                :disabled="!local.githubRepo || !local.githubToken"
                @click="onMigrateGithub"
              />
              <span
                v-if="githubMigrateResult"
                class="text-xs"
                :class="githubMigrateResult.ok ? 'text-success' : 'text-error'"
                >{{ githubMigrateResult.message }}</span
              >
            </div>
            <p class="text-xs text-dimmed">
              Logs saved by older versions keep every entry in one file, re-uploaded on every
              change. They're converted when next synced anyway - this converts them all now.
            </p>
          </UiBox>

          <!-- Map Settings (hidden for now, may be needed again later)
          <UiBox title="Map Settings">
            <SettingRow label="ACT" help="Use Altitude Colored Trail (slower at loading/changing logs)">
              <USwitch v-model="local.mapTrailAltitudeColored" size="sm" />
            </SettingRow>
          </UiBox>
          -->    

       
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2">
        <UButton
          variant="outline"
          color="neutral"
          label="Cancel"
          @click="open = false"
        />
        <UButton color="primary" label="Save" @click="onSave" />
      </div>
    </template>
  </UModal>
</template>

<script setup>
import { ref, watch, computed, toRaw } from "vue";
import UiBox from "./UiBox.vue";
import SettingRow from "./SettingRow.vue";
import PercentInput from "./PercentInput.vue";
import { useSettingsStore } from "../stores/settings.js";
import { FLIGHT_LOG_GOVSTATES, FLIGHT_LOG_AIRBORNE_STATES } from "../flightlog_fielddefs.js";
import { availableAiModels, resolveAiModel } from "../user_settings_data.js";
import { createGitHubClient } from "../github_client.js";
import { migrateLegacyLogs } from "../tuning_log_cloud.js";
import { FLIGHT_SLICE_SAMPLE_RATES, DEFAULT_FLIGHT_SLICE_SAMPLE_RATE } from "../flight_slice.js";

const open = defineModel("open", { type: Boolean, default: false });

const settingsStore = useSettingsStore();

const emit = defineEmits(["save"]);

// Deep-clone store settings into local state for editing
function cloneSettings() {
  const settings = JSON.parse(JSON.stringify(toRaw(settingsStore.userSettings))); // NOSONAR
  settings.aiModel = resolveAiModel(settings.aiModel);
  return settings;
}

const local = ref(cloneSettings());

// FLIGHT_LOG_GOVSTATES/FLIGHT_LOG_AIRBORNE_STATES are plain mutable exports (not reactive) that
// get re-populated per the currently open log's firmware version, so this list is rebuilt fresh
// each time the dialog opens rather than computed once.
function buildAutoTrimEventOptions() {
  const options = [
    { label: "Arm", value: "arm" },
    { label: "Disarm", value: "disarm" },
  ];
  for (const state of FLIGHT_LOG_GOVSTATES) {
    options.push({ label: `GovState: ${state}`, value: `govState:${state}` });
  }
  for (const state of FLIGHT_LOG_AIRBORNE_STATES) {
    options.push({ label: `Airborne: ${state}`, value: `airborne:${state}` });
  }
  return options;
}

const autoTrimEventOptions = ref(buildAutoTrimEventOptions());
const autoTrimOffsetOptions = [
  { label: "0 seconds", value: 0 },
  { label: "1/4 second", value: 0.25 },
  { label: "1/2 second", value: 0.5 },
  { label: "1 second", value: 1 },
  { label: "2 seconds", value: 2 },
];

// Re-clone when dialog opens to pick up any external changes
watch(open, (val) => {
  if (val) {
    local.value = cloneSettings();
    autoTrimEventOptions.value = buildAutoTrimEventOptions();
  }
});

// Option data
const githubTesting = ref(false);
const githubTestResult = ref(null);

watch(
  () => [local.value.githubRepo, local.value.githubBranch, local.value.githubToken],
  () => {
    githubTestResult.value = null;
  },
);

async function onTestGithub() {
  githubTesting.value = true;
  githubTestResult.value = null;
  try {
    const client = createGitHubClient({
      repo: local.value.githubRepo,
      branch: local.value.githubBranch,
      token: local.value.githubToken,
    });
    githubTestResult.value = await client.testConnection();
  } catch (error) {
    githubTestResult.value = { ok: false, message: error.message };
  } finally {
    githubTesting.value = false;
  }
}

const githubMigrating = ref(false);
const githubMigrateResult = ref(null);

async function onMigrateGithub() {
  githubMigrating.value = true;
  githubMigrateResult.value = null;
  try {
    const client = createGitHubClient({
      repo: local.value.githubRepo,
      branch: local.value.githubBranch,
      token: local.value.githubToken,
    });
    const { migrated, total } = await migrateLegacyLogs(client, {
      onProgress: (done, count) => {
        githubMigrateResult.value = { ok: true, message: `Checked ${done} of ${count} logs...` };
      },
    });
    githubMigrateResult.value = {
      ok: true,
      message: migrated ? `Converted ${migrated} of ${total} logs.` : `All ${total} logs already use one file per entry.`,
    };
  } catch (error) {
    githubMigrateResult.value = { ok: false, message: error.message };
  } finally {
    githubMigrating.value = false;
  }
}

const aiHistoryImageLimitOptions = [
  { label: "None", value: 0 },
  { label: "Last 3 entries", value: 3 },
  { label: "Last 5 entries", value: 5 },
  { label: "Last 10 entries", value: 10 },
  { label: "Last 20 entries", value: 20 },
  { label: "All entries", value: -1 },
];

const stickModeOptions = [
  { label: "Mode 1", value: 1 },
  { label: "Mode 2", value: 2 },
  { label: "Mode 3", value: 3 },
  { label: "Mode 4", value: 4 },
];

const speedOptions = [
  { label: "m/s", value: 1 },
  { label: "kph", value: 2 },
  { label: "mph", value: 3 },
];

const altitudeOptions = [
  { label: "meters", value: 1 },
  { label: "feet", value: 2 },
];

const aiModelOptions = availableAiModels.map((m) => ({
  label: m.displayName + (m.description ? ` (${m.description})` : ""),
  value: m.id,
}));

const aiEffortOptions = [
  { label: "Low (fastest, cheapest)", value: "low" },
  { label: "Medium (default)", value: "medium" },
  { label: "High", value: "high" },
  { label: "X-High", value: "xhigh" },
  { label: "Max (most thorough, slowest)", value: "max" },
];

const aiSliceSampleRateOptions = FLIGHT_SLICE_SAMPLE_RATES.map((hz) => ({
  label: hz === DEFAULT_FLIGHT_SLICE_SAMPLE_RATE ? `${hz} Hz (default)` : `${hz} Hz`,
  value: hz,
}));

const darkModeOptions = [
  { label: "Auto (system)", value: 2 },
  { label: "Off (light)", value: 1 },
  { label: "On (dark)", value: 0 },
];

const craftFacingOptions = [
  { label: "Facing Left", value: "left" },
  { label: "Facing Forwards", value: "forward" },
  { label: "Facing Right", value: "right" },
  { label: "Facing Backwards", value: "backward" },
];

// Overlay position rows — driven by local state so grid columns align
const positionRows = computed(() => {
  const rows = [
    { label: "Sticks", obj: local.value.sticks, topKey: "top", leftKey: "left", sizeKey: "size" },
    { label: "Craft", obj: local.value.craft, topKey: "top", leftKey: "left", sizeKey: "size" },
    { label: "Analyser", obj: local.value.analyser, topKey: "top", leftKey: "left", sizeKey: "size" },
    { label: "Anlsr legend", obj: local.value.analyser_legend, topKey: "top", leftKey: "left", sizeKey: "width", sizeLabel: "W" },
    { label: "Step response", obj: local.value.stepResponse, topKey: "top", leftKey: "left", sizeKey: "size" },
    { label: "Map", obj: local.value.map, topKey: "top", leftKey: "left", sizeKey: "size" },
  ];
  if (local.value.drawWatermark) {
    rows.push({ label: "Watermark", obj: local.value.watermark, topKey: "top", leftKey: "left", sizeKey: "size" });
  }
  return rows;
});

function pv(v) {
  return Number.parseInt(v) || 0;
}

function onLogoChange(e) {
  const file = e.target.files?.[0];
  if (!file) { return; }
  const reader = new FileReader();
  reader.onload = (ev) => {
    local.value.watermark.logo = ev.target.result;
  };
  reader.readAsDataURL(file);
}

function onSave() {
  const raw = JSON.parse(JSON.stringify(toRaw(local.value))); // NOSONAR
  raw.aiSkillIds = (raw.aiSkillIds || []).map((id) => id.trim()).filter(Boolean);
  for (const key of ["githubRepo", "githubBranch", "githubToken"]) {
    raw[key] = (raw[key] || "").trim();
  }
  emit("save", raw);
  open.value = false;
}
</script>
